import { Layers, Plus, Trash2, X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import {
  useCreateJobTemplate,
  useCurrentUser,
  useDeleteJobTemplate,
  useJobTemplates,
  useUpdateJobTemplate,
} from "@/api/hooks";
import type { JobTemplateResponse, TemplateLineItem } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/** Saved-scope manager for a contractor org. List on the left, inline
 *  editor on the right (or full-width "new" form when creating).
 *
 *  Templates seed the New Job dialog (title/description) and the quote
 *  editor's line-item list. The seeding happens client-side at the
 *  call sites; this page is the catalog. */
export function JobTemplatesPage() {
  const me = useCurrentUser();
  const myRole = me.data?.role;
  const canManage = myRole === "pencilout_admin" || myRole === "org_admin";

  const list = useJobTemplates();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // When the list refreshes after a create/edit, ensure the selection
  // still maps to a real row. Auto-select the first row if nothing is
  // selected yet — gives the page a non-empty initial state.
  useEffect(() => {
    if (!list.data) return;
    if (selectedId && !list.data.find((t) => t.id === selectedId)) {
      setSelectedId(list.data[0]?.id ?? null);
    } else if (!selectedId && list.data.length > 0) {
      const first = list.data[0];
      if (first) setSelectedId(first.id);
    }
  }, [list.data, selectedId]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-mono text-2xl font-bold tracking-tight">Job templates</h1>
          <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
            Reusable starter packages for new jobs — title, description, and starter line items the
            quote editor pulls from. One template per common job type ("Kitchen Reno", "Master
            Bath") saves retyping the same scope every time.
          </p>
        </div>
        {canManage && !creating && (
          <button
            type="button"
            onClick={() => {
              setCreating(true);
              setSelectedId(null);
            }}
            className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> New template
          </button>
        )}
      </div>

      {list.isLoading && (
        <div className="rounded-lg border border-rule bg-paper-warm p-6 font-mono text-sm text-graphite-soft">
          Loading…
        </div>
      )}
      {list.isError && (
        <div className="rounded-lg border border-red-pencil/30 bg-red-pencil/5 p-4 font-mono text-sm text-red-pencil">
          {list.error?.message ?? "Failed to load templates."}
        </div>
      )}

      {creating && canManage && (
        <TemplateForm
          mode="create"
          onClose={(savedId) => {
            setCreating(false);
            if (savedId) setSelectedId(savedId);
          }}
        />
      )}

      {list.data && list.data.length === 0 && !creating && (
        <div className="rounded-lg border border-dashed border-rule bg-paper-warm p-10 text-center">
          <Layers className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-3 font-mono text-sm font-semibold text-graphite">No templates yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-graphite-soft">
            Build your first one for the kind of job you quote most often. You'll thank yourself the
            next time you start one.
          </p>
        </div>
      )}

      {list.data && list.data.length > 0 && !creating && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
          <TemplateList templates={list.data} selectedId={selectedId} onSelect={setSelectedId} />
          {(() => {
            const selected = selectedId ? list.data.find((t) => t.id === selectedId) : undefined;
            if (selected) {
              return (
                <TemplateForm
                  key={selectedId}
                  mode="edit"
                  template={selected}
                  canManage={canManage}
                  onClose={() => {}}
                />
              );
            }
            return (
              <div className="rounded-lg border border-rule bg-paper-warm p-6 font-mono text-sm text-graphite-soft">
                Pick a template on the left to edit.
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// List column
// ---------------------------------------------------------------

function TemplateList({
  templates,
  selectedId,
  onSelect,
}: {
  templates: JobTemplateResponse[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <ul className="divide-y divide-rule overflow-hidden rounded-lg border border-rule bg-paper-warm">
      {templates.map((t) => {
        const isActive = t.id === selectedId;
        const itemCount = t.default_line_items.length;
        return (
          <li key={t.id}>
            <button
              type="button"
              onClick={() => onSelect(t.id)}
              className={`flex w-full items-start gap-3 p-3 text-left transition hover:bg-paper ${isActive ? "bg-paper" : ""}`}
            >
              <Layers
                className={`mt-0.5 h-4 w-4 shrink-0 ${isActive ? "text-graphite" : "text-graphite-faint"}`}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-sm font-semibold text-graphite">
                  {t.name}
                </span>
                <span className="mt-0.5 block font-mono text-[11px] text-graphite-soft">
                  {itemCount === 0
                    ? "No starter items"
                    : `${itemCount} starter item${itemCount === 1 ? "" : "s"}`}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------
// Editor / creator form
// ---------------------------------------------------------------

function TemplateForm(
  props:
    | {
        mode: "create";
        onClose: (savedId?: string) => void;
      }
    | {
        mode: "edit";
        template: JobTemplateResponse;
        canManage: boolean;
        onClose: (savedId?: string) => void;
      },
) {
  const initial =
    props.mode === "edit"
      ? props.template
      : {
          name: "",
          default_title: null,
          default_description: null,
          default_line_items: [] as TemplateLineItem[],
        };

  const [name, setName] = useState(initial.name);
  const [defaultTitle, setDefaultTitle] = useState(initial.default_title ?? "");
  const [defaultDescription, setDefaultDescription] = useState(initial.default_description ?? "");
  const [items, setItems] = useState<TemplateLineItem[]>(initial.default_line_items);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const create = useCreateJobTemplate();
  const update = useUpdateJobTemplate(props.mode === "edit" ? props.template.id : "");
  const del = useDeleteJobTemplate();
  const canManage = props.mode === "edit" ? props.canManage : true;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const payload = {
      name: name.trim(),
      default_title: defaultTitle.trim() || null,
      default_description: defaultDescription.trim() || null,
      default_line_items: items,
    };

    if (props.mode === "create") {
      create.mutate(payload, {
        onSuccess: (saved) => props.onClose(saved.id),
        onError: (err) => setError(err.message),
      });
    } else {
      update.mutate(payload, {
        onError: (err) => setError(err.message),
      });
    }
  }

  function addItem() {
    setItems((prev) => [
      ...prev,
      {
        category: "",
        description: "",
        quantity: 1,
        unit: "ea",
        unit_price_cents: 0,
        visible_to_client: true,
      },
    ]);
  }

  function updateItem(idx: number, patch: Partial<TemplateLineItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  const isPending = create.isPending || update.isPending;
  const isCreate = props.mode === "create";

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 rounded-lg border border-rule bg-paper-warm p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
            {isCreate ? "New template" : "Edit template"}
          </h2>
          <p className="mt-1 text-sm text-graphite-soft">
            {isCreate
              ? "Give it a recognizable name. The default title and description pre-fill the New Job dialog; starter items pre-fill the quote editor."
              : "Changes save when you click Save. Use this template by selecting it in the New Job dialog."}
          </p>
        </div>
        {!isCreate && (
          <button
            type="button"
            onClick={() => props.onClose()}
            className="rounded border border-rule p-1.5 text-graphite-soft transition hover:bg-paper hover:text-graphite"
            aria-label="Close editor"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      <div className="space-y-4">
        <FormField
          id="template-name"
          label="Name"
          required
          help="Up to 120 chars. Free-form per-org."
        >
          <input
            id="template-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canManage}
            maxLength={120}
            placeholder="Kitchen Reno"
            className={fieldClass}
          />
        </FormField>

        <FormField
          id="template-default-title"
          label="Default job title"
          help="Pre-fills the title field on New Job. Leave blank to use the template name."
        >
          <input
            id="template-default-title"
            type="text"
            value={defaultTitle}
            onChange={(e) => setDefaultTitle(e.target.value)}
            disabled={!canManage}
            maxLength={255}
            placeholder="Kitchen Reno – {client}"
            className={fieldClass}
          />
        </FormField>

        <FormField
          id="template-default-description"
          label="Default scope description"
          help="Pre-fills the job description. Free-form prose."
        >
          <textarea
            id="template-default-description"
            value={defaultDescription}
            onChange={(e) => setDefaultDescription(e.target.value)}
            disabled={!canManage}
            maxLength={10000}
            rows={4}
            placeholder="Full kitchen tear-out + refit. Includes appliances, counters, and lighting."
            className={fieldClass}
          />
        </FormField>
      </div>

      <div className="rounded border border-rule bg-paper p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Starter line items
          </h3>
          <span className="font-mono text-[11px] text-graphite-faint">{items.length} / 50</span>
        </div>
        <p className="mt-1 max-w-prose text-xs text-graphite-soft">
          Common items the AI seeds (and you can override) when this template's job generates a
          quote. ``unit_price_cents = 0`` for items priced per-job.
        </p>

        <ul className="mt-3 space-y-2">
          {items.map((item, idx) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: TemplateLineItem has no stable id and every text field is user-editable; index is the only stable identifier across edits/adds/removes within a single mounted form.
              key={idx}
              className="flex flex-wrap items-start gap-2 rounded border border-rule bg-paper-warm p-2"
            >
              <input
                type="text"
                value={item.category}
                onChange={(e) => updateItem(idx, { category: e.target.value })}
                placeholder="Category"
                disabled={!canManage}
                maxLength={120}
                className={`${fieldClass} flex-1 min-w-[8rem]`}
              />
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateItem(idx, { description: e.target.value })}
                placeholder="Description"
                disabled={!canManage}
                maxLength={500}
                className={`${fieldClass} flex-[2] min-w-[12rem]`}
              />
              <input
                type="number"
                value={item.quantity}
                onChange={(e) =>
                  updateItem(idx, {
                    quantity: Number(e.target.value) || 0,
                  })
                }
                min={0}
                step={0.01}
                disabled={!canManage}
                className={`${fieldClass} w-20`}
                placeholder="Qty"
              />
              <input
                type="text"
                value={item.unit}
                onChange={(e) => updateItem(idx, { unit: e.target.value })}
                placeholder="ea"
                disabled={!canManage}
                maxLength={32}
                className={`${fieldClass} w-16`}
              />
              <div className="flex items-center gap-1">
                <span className="font-mono text-[11px] text-graphite-soft">$</span>
                <input
                  type="number"
                  value={(item.unit_price_cents / 100).toFixed(2)}
                  onChange={(e) =>
                    updateItem(idx, {
                      unit_price_cents: Math.round(Number(e.target.value || 0) * 100),
                    })
                  }
                  min={0}
                  step={0.01}
                  disabled={!canManage}
                  className={`${fieldClass} w-24`}
                  placeholder="0.00"
                />
              </div>
              <label className="inline-flex items-center gap-1 font-mono text-[11px] text-graphite-soft">
                <input
                  type="checkbox"
                  checked={item.visible_to_client}
                  onChange={(e) =>
                    updateItem(idx, {
                      visible_to_client: e.target.checked,
                    })
                  }
                  disabled={!canManage}
                />
                Visible
              </label>
              {canManage && (
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="rounded p-1 text-graphite-soft transition hover:bg-red-pencil/10 hover:text-red-pencil"
                  aria-label="Remove line"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>

        {canManage && items.length < 50 && (
          <button
            type="button"
            onClick={addItem}
            className="mt-3 inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-paper-warm"
          >
            <Plus className="h-3 w-3" aria-hidden /> Add line item
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {canManage && (
          <button
            type="submit"
            disabled={isPending}
            className="rounded bg-graphite px-4 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
          >
            {isPending ? "Saving…" : isCreate ? "Create template" : "Save"}
          </button>
        )}
        {isCreate && (
          <button
            type="button"
            onClick={() => props.onClose()}
            className="rounded border border-rule bg-paper px-4 py-2 font-mono text-sm font-semibold text-graphite transition hover:bg-paper-warm"
          >
            Cancel
          </button>
        )}
        {!isCreate && canManage && (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-1.5 font-mono text-xs font-semibold text-red-pencil transition hover:bg-red-pencil/10"
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Delete
          </button>
        )}
        {error && <span className="font-mono text-xs text-red-pencil">{error}</span>}
      </div>

      {confirmDelete && props.mode === "edit" && (
        <ConfirmDialog
          title="Delete template?"
          body={`"${props.template.name}" will be removed. Jobs already created from this template are unaffected.`}
          confirmLabel="Delete"
          variant="danger"
          busy={del.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            del.mutate(props.template.id, {
              onSuccess: () => {
                setConfirmDelete(false);
                props.onClose();
              },
              onError: (err) => {
                setError(err.message);
                setConfirmDelete(false);
              },
            });
          }}
        />
      )}
    </form>
  );
}

// ---------------------------------------------------------------
// Reused atoms
// ---------------------------------------------------------------

const fieldClass =
  "w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none disabled:opacity-60";

function FormField({
  id,
  label,
  required,
  help,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
      >
        {label}
        {required && <span className="ml-0.5 text-red-pencil">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {help && <p className="mt-1 font-mono text-[11px] text-graphite-faint">{help}</p>}
    </div>
  );
}
